import type {
  V1Event,
  V1Phase,
  V1Plan,
  V1Step,
  V1StepStatus,
} from '@/generated/hooks/rain/models';

import { ERainPlanTool } from '../constants';

import type { IDeletedEventRange } from './rollback';
import { rainv1ValueToJs } from './value';

/** A plan rebuilt from the thread's own `plan_*` tool calls. */
export interface IRainStreamPlan {
  id: string;
  /** Event id of the `plan_create` that opened it, which positions its card. */
  createdAtEventId: number;
  lastEventId: number;
  plan: V1Plan;
  /** Destroyed by `plan_delete`, or displaced by a later `plan_create`. */
  isRetired: boolean;
}

interface IPendingPlanCall {
  name: string;
  args: Record<string, unknown>;
}

export interface IRainPlanStreamState {
  plans: IRainStreamPlan[];
  callsById: Map<string, IPendingPlanCall>;
  /** Tool call index → id. Arguments carry only an index, never an id. */
  callIdByIndex: Map<number, string>;
  /** Every user turn, so a rollback can find the newest one that survived. */
  userEventIds: number[];
  /** Replay guard, keyed by tool call rather than event: plan mutations resolve
   *  the live plan by position, so a re-delivered one would land on a different
   *  plan than it first did. Value is the event id, for rollback pruning. */
  appliedCalls: Map<string, number | undefined>;
  /** Newest event id folded in, the anchor for a result that carries none. */
  lastSeenEventId: number;
  lastUserEventId: number;
}

export const createPlanStreamState = (): IRainPlanStreamState => ({
  plans: [],
  callsById: new Map(),
  callIdByIndex: new Map(),
  userEventIds: [],
  appliedCalls: new Map(),
  lastSeenEventId: -1,
  lastUserEventId: -1,
});

// The agent sends title-case labels, not the enum the REST model uses. Matched
// case-insensitively so a casing change upstream doesn't drop every update.
const STATUS_BY_LABEL: Record<string, V1StepStatus> = {
  pending: 'STEP_STATUS_PENDING',
  completed: 'STEP_STATUS_COMPLETED',
  failed: 'STEP_STATUS_FAILED',
  skipped: 'STEP_STATUS_SKIPPED',
};

const isPlanTool = (name: string | undefined): boolean =>
  typeof name === 'string' && name.startsWith('plan_');

const asRecord = (value: unknown): Record<string, unknown> | undefined =>
  typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;

const asString = (value: unknown): string | undefined =>
  typeof value === 'string' ? value : undefined;

function toPlanSteps(value: unknown): V1Step[] {
  if (!Array.isArray(value)) return [];
  const steps: V1Step[] = [];
  for (const entry of value) {
    const fields = asRecord(entry);
    if (!fields) continue;
    steps.push({
      name: asString(fields.name),
      description: asString(fields.description),
      acceptance_criteria: asString(fields.acceptance_criteria),
      status: 'STEP_STATUS_PENDING',
    });
  }
  return steps;
}

function toPlanPhases(value: unknown): V1Phase[] {
  if (!Array.isArray(value)) return [];
  const phases: V1Phase[] = [];
  for (const entry of value) {
    const fields = asRecord(entry);
    if (!fields) continue;
    phases.push({
      name: asString(fields.name),
      description: asString(fields.description),
      steps: toPlanSteps(fields.steps),
    });
  }
  return phases;
}

/** `"3.2"` → phase 3, step 2, both 1-indexed on the wire. */
function parseStepTarget(
  target: string | undefined,
): { phaseIndex: number; stepIndex: number } | undefined {
  if (!target) return undefined;
  const match = /^(\d+)\.(\d+)$/.exec(target.trim());
  if (!match) return undefined;
  const phaseIndex = Number(match[1]) - 1;
  const stepIndex = Number(match[2]) - 1;
  if (phaseIndex < 0 || stepIndex < 0) return undefined;
  return { phaseIndex, stepIndex };
}

const livePlan = (state: IRainPlanStreamState): IRainStreamPlan | undefined => {
  for (let i = state.plans.length - 1; i >= 0; i -= 1) {
    if (!state.plans[i].isRetired) return state.plans[i];
  }
  return undefined;
};

function applyPlanCreate(
  state: IRainPlanStreamState,
  call: IPendingPlanCall,
  eventId: number | undefined,
): boolean {
  // Only creation needs the real id: it names the card, positions it, and is
  // what rollback matches on. The mutations below accept a derived anchor.
  if (eventId === undefined) return false;

  for (const existing of state.plans) existing.isRetired = true;
  state.plans.push({
    id: `rain-plan-${eventId}`,
    createdAtEventId: eventId,
    lastEventId: eventId,
    isRetired: false,
    plan: {
      name: asString(call.args.name),
      description: asString(call.args.description),
      active: false,
      phases: toPlanPhases(call.args.phases),
    },
  });
  return true;
}

function applyMarkStep(
  state: IRainPlanStreamState,
  call: IPendingPlanCall,
  anchor: number,
): boolean {
  const target = parseStepTarget(asString(call.args.target));
  const status =
    STATUS_BY_LABEL[asString(call.args.status)?.toLowerCase() ?? ''];
  const current = livePlan(state);
  if (!target || !status || !current) return false;

  const phase = current.plan.phases?.[target.phaseIndex];
  const step = phase?.steps?.[target.stepIndex];
  if (!step) return false;

  step.status = status;
  const reason = asString(call.args.reason);
  if (reason) step.failure_reason = reason;
  current.lastEventId = anchor;
  return true;
}

/** A `target` removes one step rather than the plan — shape unobserved, so skipped. */
function applyPlanDelete(
  state: IRainPlanStreamState,
  call: IPendingPlanCall,
): boolean {
  if (asString(call.args.target)) return false;
  const current = livePlan(state);
  if (!current) return false;
  current.isRetired = true;
  return true;
}

function applyCompletedCall(
  state: IRainPlanStreamState,
  call: IPendingPlanCall,
  eventId: number | undefined,
): boolean {
  // A result with no id of its own still happened after everything already
  // folded in, so the newest event seen is a sound stand-in. Leaving the anchor
  // where it was would strand the card cache and settle the plan a turn early.
  const anchor = eventId ?? state.lastSeenEventId;
  switch (call.name) {
    case ERainPlanTool.PlanCreate:
      return applyPlanCreate(state, call, eventId);
    case ERainPlanTool.PlanActivate: {
      const current = livePlan(state);
      if (!current) return false;
      current.plan.active = true;
      current.lastEventId = anchor;
      return true;
    }
    case ERainPlanTool.PlanMarkStep:
      return applyMarkStep(state, call, anchor);
    case ERainPlanTool.PlanDelete:
      return applyPlanDelete(state, call);
    default:
      return false;
  }
}

/**
 * Folds one event in, returning true when the rendered set of plans changed.
 * Mutations apply on `tool_result`: arguments stream in one at a time, and only
 * the result proves the server accepted them.
 */
export function applyPlanEvent(
  state: IRainPlanStreamState,
  event: V1Event,
): boolean {
  // Root only: the pinned card reads the root thread's plan, so folding a
  // sub-agent's would retire a plan the server still considers active.
  if (event.task_id) return false;

  const eventId = Number(event.id);
  // Falsy, not just undefined: a gateway emitting proto3 defaults sends an
  // empty string for an unset id, and `Number('')` is 0 — a real event id that
  // would anchor a plan to the top of the thread. `'0'` is truthy and survives.
  const hasEventId = Boolean(event.id) && Number.isFinite(eventId);
  if (hasEventId) {
    state.lastSeenEventId = Math.max(state.lastSeenEventId, eventId);
  }

  if (event.input_text && hasEventId) {
    if (state.userEventIds.includes(eventId)) return false;
    state.userEventIds.push(eventId);
    state.lastUserEventId = Math.max(state.lastUserEventId, eventId);
    return state.plans.length > 0;
  }

  const start = event.tool_call_start;
  if (start) {
    if (!isPlanTool(start.name) || !start.id || start.index === undefined) {
      return false;
    }
    state.callsById.set(start.id, { name: start.name ?? '', args: {} });
    state.callIdByIndex.set(start.index, start.id);
    return false;
  }

  const argument = event.tool_call_argument;
  if (argument) {
    if (argument.index === undefined || !argument.name) return false;
    const callId = state.callIdByIndex.get(argument.index);
    const call = callId ? state.callsById.get(callId) : undefined;
    if (!call) return false;
    call.args[argument.name] = rainv1ValueToJs(argument.value) ?? undefined;
    return false;
  }

  const result = event.tool_result;
  if (result?.tool_call_id) {
    const call = state.callsById.get(result.tool_call_id);
    if (!call) return false;
    state.callsById.delete(result.tool_call_id);
    if (!result.success) return false;
    if (state.appliedCalls.has(result.tool_call_id)) return false;

    const anchorId = hasEventId ? eventId : undefined;
    const applied = applyCompletedCall(state, call, anchorId);
    // Only a call that landed burns its id. A declined one changed nothing, so
    // re-delivering it — with a usable id this time — has to be allowed.
    if (applied) state.appliedCalls.set(result.tool_call_id, anchorId);
    return applied;
  }

  return false;
}

/** True once no step is still waiting to be worked — failures included. */
export function isPlanResolved(plan: V1Plan | undefined): boolean {
  const phases = plan?.phases ?? [];
  let total = 0;
  for (const phase of phases) {
    for (const step of phase.steps ?? []) {
      total += 1;
      const status = step.status;
      if (
        !status ||
        status === 'STEP_STATUS_PENDING' ||
        status === 'STEP_STATUS_UNSPECIFIED'
      ) {
        return false;
      }
    }
  }
  return total > 0;
}

const belongsToStream = (
  entry: IRainStreamPlan,
  state: IRainPlanStreamState,
): boolean =>
  entry.isRetired ||
  (isPlanResolved(entry.plan) && state.lastUserEventId > entry.lastEventId);

const hasSteps = (plan: V1Plan): boolean =>
  (plan.phases ?? []).some(phase => (phase.steps ?? []).length > 0);

/** Plans the transcript should carry, oldest first. */
export function selectStreamPlans(
  state: IRainPlanStreamState,
): IRainStreamPlan[] {
  // A stepless plan renders an empty "Todo · 0 of 0" card with nothing to open.
  return state.plans.filter(
    entry => hasSteps(entry.plan) && belongsToStream(entry, state),
  );
}

/**
 * True once the newest plan has moved to the transcript, so the pinned card
 * stands down. Read off the log, not the card, so it survives a reload.
 */
export function isLivePlanRetired(state: IRainPlanStreamState): boolean {
  const newest = state.plans.at(-1);
  return newest === undefined ? false : belongsToStream(newest, state);
}

/**
 * Forgets plans whose creating events a rollback deleted.
 *
 * Partial: a plan created outside the range keeps step statuses set inside it,
 * and stays retired if the call that retired it was rolled back.
 *
 * @returns Whether anything was dropped
 */
export function rollbackPlanEvents(
  state: IRainPlanStreamState,
  range: IDeletedEventRange,
): boolean {
  const kept = state.plans.filter(
    entry =>
      entry.createdAtEventId < range.from || entry.createdAtEventId > range.to,
  );
  const droppedPlans = kept.length !== state.plans.length;

  // Recomputed, not clamped to `range.from - 1`: the newest surviving turn can
  // be far older, and a clamp there would strand the card in the transcript.
  const keptTurns = state.userEventIds.filter(
    id => id < range.from || id > range.to,
  );
  const droppedTurn = keptTurns.length !== state.userEventIds.length;

  if (!droppedPlans && !droppedTurn) return false;

  state.plans = kept;
  if (droppedTurn) {
    state.userEventIds = keptTurns;
    state.lastUserEventId = keptTurns.reduce(
      (max, id) => Math.max(max, id),
      -1,
    );
  }
  // An id-less entry can't be matched to the range, and keeping one silently
  // drops the retry's re-delivery. Dropping it risks a double-apply instead,
  // which the plan-position checks absorb; a stuck pending step is permanent.
  for (const [callId, id] of state.appliedCalls) {
    if (id === undefined || (id >= range.from && id <= range.to)) {
      state.appliedCalls.delete(callId);
    }
  }

  // Left inside the deleted range, this anchors a later id-less result above
  // every surviving turn — the stall this function exists to prevent.
  if (
    state.lastSeenEventId >= range.from &&
    state.lastSeenEventId <= range.to
  ) {
    state.lastSeenEventId = range.from - 1;
  }
  return true;
}
